// SaveTemplateDialog - Save current workflow configuration as a reusable template
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api-client';
import { Save, FileText, Calculator, CreditCard, Banknote, Building2 } from 'lucide-react';
import type { WorkflowData } from '@/contexts/WorkflowContext';
import { TemplateForm } from './TemplateForm';
import { buildTemplateConfig } from '@/lib/template-utils';

interface SaveTemplateDialogProps {
  workflowData: WorkflowData;
  trigger?: React.ReactNode;
}

export function SaveTemplateDialog({ workflowData, trigger }: SaveTemplateDialogProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const configPreview = buildTemplateConfig(workflowData);

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: async (templateData: {
      name: string;
      description?: string;
      config: Record<string, unknown>;
      isDefault: boolean;
    }) => {
      return api.templates.create(templateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast({
        title: 'Template Saved',
        description: 'Your workflow configuration has been saved as a template.',
      });
      setOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save template',
        variant: 'destructive',
      });
    },
  });

  // Calculate what's included in the template
  const includedItems = [
    {
      icon: FileText,
      label: 'Summary Preferences',
      included: true, // Always included in our global config
      color: 'text-accent-500',
    },
    {
      icon: Calculator,
      label: 'Financial Metrics',
      included: configPreview.modules.financial.enabled,
      count: configPreview.modules.financial.selectedMetricIds.length,
      color: 'text-blue-500',
    },
    {
      icon: Calculator,
      label: 'Custom Ratios',
      included: configPreview.modules.financial.enabled && configPreview.modules.financial.customRatios.length > 0,
      count: configPreview.modules.financial.customRatios.length,
      color: 'text-cyan-500',
    },
    {
      icon: CreditCard,
      label: 'Credit Report Configuration',
      included: configPreview.modules.creditReport?.enabled,
      color: 'text-purple-500',
    },
    {
      icon: Banknote,
      label: 'Bank Statement Analysis',
      included: configPreview.modules.bank.enabled,
      color: 'text-green-500',
    },
    {
      icon: Building2,
      label: 'Corporate Search',
      included: configPreview.modules.corporate.enabled,
      color: 'text-orange-500',
    },
  ];

  const activeItems = includedItems.filter((item) => item.included);

  const handleFormSave = (payload: { name: string; description?: string; isDefault: boolean }) => {
    // Build template config from workflow data then call mutation
    const finalConfig = buildTemplateConfig(workflowData);

    saveMutation.mutate({
      name: payload.name,
      description: payload.description,
      config: finalConfig as any,
      isDefault: payload.isDefault,
    });
  };

  return (
    <TemplateForm
      open={open}
      onOpenChange={setOpen}
      onSave={handleFormSave}
      isSaving={saveMutation.isPending}
      includedItems={activeItems}
      trigger={trigger || (
        <Button variant="outline" size="lg">
          <Save className="w-4 h-4 mr-2" />
          Save as Template
        </Button>
      )}
    />
  );
}
